import { useEffect, useMemo, useState } from 'react'
import { optimisticRemoveKey, useApp, useWithoutRemoved } from '@/core/state/appStore'
import { currentVariant } from '@/core/data/session'
import { listMassnahmenFull, type MassnahmeRow } from '@/core/data/massnahmen'
import { listMassnahmeUeSums } from '@/variants/azav/data/modul'
import { ListView } from '@/core/views/ListView'
import { descriptionFor } from '@/variants'
import { massnahmeViewConfigWithUeSums, massnahmeViewConfigMinimal } from './massnahmeViewConfig'
import { MassnahmeModuln } from '@/variants/azav/components/orgsettings/MassnahmeModuln'

export function MassnahmenSection() {
  const { state, dispatch, reloadMassnahmen, identity } = useApp()

  const hasModules = descriptionFor(currentVariant()).hasModules
  const organisationId = identity?.organisationId
  const isOwner = identity?.isOwner ?? false
  const [massnahmen, setMassnahmen] = useState<MassnahmeRow[]>([])
  const [ueSums, setUeSums] = useState<Map<string, number>>(new Map())

  useEffect(() => {
    if (!organisationId) return
    let cancelled = false
    listMassnahmenFull(organisationId)
      .then((rows) => {
        if (!cancelled) setMassnahmen(rows)
      })
      .catch((e) => console.error("Maßnahmen laden fehlgeschlagen:", e))
    return () => {
      cancelled = true
    }
  }, [organisationId, state.massnahmenVersion])

  useEffect(() => {
    if (!organisationId || !hasModules) return
    let cancelled = false
    listMassnahmeUeSums()
      .then((sums) => {
        if (!cancelled) setUeSums(sums)
      })
      .catch((e) => console.error("UE-Summen laden fehlgeschlagen:", e))
    return () => {
      cancelled = true
    }
  }, [organisationId, state.massnahmenVersion, hasModules])

  const shown = useWithoutRemoved(massnahmen, (m) => optimisticRemoveKey('massnahme', m.id))

  const config = useMemo(
    () => (hasModules ? massnahmeViewConfigWithUeSums(ueSums) : massnahmeViewConfigMinimal),
    [ueSums, hasModules],
  )

  const onRowClick = (massnahme: MassnahmeRow) => {
    dispatch({ type: 'SET_CONTROL', key: 'selectedModulId', value: null })
    const source = { kind: 'row' as const }
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'massnahme', massnahmeId: massnahme.id, tab: 'details', coachingId: null, source } })
  }

  return (
    <ListView
      items={shown}
      config={config}
      viewState={{ sort: state.massnahmeSort, search: state.massnahmeSearch, filter: state.massnahmeViewFilter }}
      selectedKey={state.selectedMassnahmeId}
      onRowClick={onRowClick}
      renderExpansion={
        organisationId && hasModules
          ? (massnahme) => (
              <MassnahmeModuln
                massnahmeId={massnahme.id}
                organisationId={organisationId}
                isOwner={isOwner}
                version={state.massnahmenVersion}
                onChanged={reloadMassnahmen}
              />
            )
          : undefined
      }
    />
  )
}
